import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon, ArrowRightIcon, BadgeCheckIcon, BookOpenIcon, HandCoinsIcon, LifeBuoyIcon, PackageSearchIcon, RotateCcwIcon, ScanLineIcon, ShieldCheckIcon, BoxIcon } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { SiteFooter } from "../components/SiteFooter";
import { OwnerForm } from "../components/OwnerForm";
import { OwnerFormId, ownerForms } from "../data/ownerForms";
import { trackCustom } from "../utils/tracking";
type Tile = {
  kind: 'form';
  id: OwnerFormId;
  icon: BoxIcon;
  title: string;
  body: string;
} | {
  kind: 'page';
  to: string;
  icon: BoxIcon;
  title: string;
  body: string;
};
const tiles: Tile[] = [{
  kind: 'page',
  to: '/activate',
  icon: BadgeCheckIcon,
  title: 'Activate your X1',
  body: 'Register your X1 in a minute.'
}, {
  kind: 'page',
  to: '/verify',
  icon: ScanLineIcon,
  title: 'Verify authenticity',
  body: 'Check your production batch code.'
}, {
  kind: 'form',
  id: 'warranty',
  icon: ShieldCheckIcon,
  title: 'Replacement claim',
  body: 'Broke it? We replace it.'
}, {
  kind: 'form',
  id: 'return',
  icon: RotateCcwIcon,
  title: 'Return request',
  body: '30 days to send it back.'
}, {
  kind: 'form',
  id: 'rewards',
  icon: HandCoinsIcon,
  title: 'Crew Rewards',
  body: 'Share GritVue. Get paid.'
}, {
  kind: 'form',
  id: 'support',
  icon: LifeBuoyIcon,
  title: 'Contact support',
  body: 'A real person answers.'
}, {
  kind: 'page',
  to: '/track',
  icon: PackageSearchIcon,
  title: 'Track my order',
  body: 'Follow it on USPS.'
}, {
  kind: 'page',
  to: '/guide',
  icon: BookOpenIcon,
  title: 'How to use',
  body: 'Step-by-step quick start.'
}];
const isFormId = (value: string | null): value is OwnerFormId => Boolean(value && value in ownerForms);

/** /owner · pick an option; forms open on their own view with a back arrow. */
export function OwnerCenter() {
  const [params, setParams] = useSearchParams();
  const requested = params.get('form');
  const active: OwnerFormId | null = isFormId(requested) ? requested : null;
  useEffect(() => {
    trackCustom('owner_center_visited');
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [active]);
  const open = (id: OwnerFormId) => {
    trackCustom('owner_form_opened', {
      form: id
    });
    setParams({
      form: id
    });
  };
  const back = () => setParams({});
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1926" data-mp="21hcf12f30mf4">
      <PageHeader data-id="element-1927" data-mp="1wgor4pm5glp4" />
      <main className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8 lg:py-14" data-id="element-1928" data-mp="1wi9mghgag8o1">
        {active ? <div className="max-w-3xl" data-id="element-1929" data-mp="1rndicixs1uuk">
            <button type="button" onClick={back} className="mb-6 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.16em] text-steel-300 transition-colors duration-150 ease-grit hover:text-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1930" data-mp="1mqwvrt687ojl">
              <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-1931" data-mp="2nnygz4btbr42" />
              Back to Owner center
            </button>
            <section aria-live="polite" data-id="element-1932" data-mp="2rqbkdm337cxo">
              <OwnerForm key={active} config={ownerForms[active]} data-id="element-1933" data-mp="2rrcy5ybhmdj3" />
            </section>
          </div> : <>
            <Link to="/" className="mb-6 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.16em] text-steel-300 transition-colors duration-150 ease-grit hover:text-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1934" data-mp="2wqptz8kc6uxt">
              <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-1935" data-mp="3aml8713sjhuv" />
              Back to home
            </Link>
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-1936" data-mp="3fgxsfdd3cx7o">Owner center</p>
            <h1 className="grit-headline mt-3 text-[clamp(2.5rem,8vw,4.5rem)] font-extrabold uppercase leading-[0.92]" data-id="element-1937" data-mp="3kfs9oaveudpo">
              Your X1. <span className="text-grit-500" data-id="element-1938" data-mp="3ublusin73fj2">Handled.</span>
            </h1>
            <p className="mt-4 max-w-2xl text-[16px] leading-relaxed text-steel-300" data-id="element-1939" data-mp="3u9rc7azebzi5">
              Activate, verify, claim a replacement, return, join Crew Rewards or talk to us. Pick what you need.
            </p>

            <ul className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4" data-id="element-1940" data-mp="14no3tgaz13m9">
              {tiles.map(tile => {
            const Icon = tile.icon;
            const className = 'flex h-full w-full items-start gap-3 border border-white/12 bg-ink-900 p-4 text-left transition-colors duration-150 ease-grit hover:border-grit-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500';
            const inner = <>
                    <Icon className="mt-0.5 h-6 w-6 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1941" data-mp="l0ztbvan8m37" />
                    <span className="min-w-0" data-id="element-1942" data-mp="gxa7vep9mkme">
                      <span className="block font-display text-lg font-bold uppercase leading-tight text-offwhite" data-id="element-1943" data-mp="h0pr8tpz2ftm">{tile.title}</span>
                      <span className="mt-0.5 block text-[13px] leading-snug text-steel-400" data-id="element-1944" data-mp="1se1pmp2yq68e">{tile.body}</span>
                    </span>
                    <ArrowRightIcon className="ml-auto mt-1 h-4 w-4 shrink-0 text-steel-500" aria-hidden="true" data-id="element-1945" data-mp="1zvgc4ygn02qq" />
                  </>;
            return <li key={tile.title} data-id="element-1946" data-mp="1pyv567l24hk9">
                    {tile.kind === 'form' ? <button type="button" onClick={() => open(tile.id)} className={className} data-id="element-1947" data-mp="1q0eok720f8q9">
                        {inner}
                      </button> : <Link to={tile.to} state={{
                fromOwner: true
              }} className={className} data-id="element-1948" data-mp="2insma7dj88bf">
                        {inner}
                      </Link>}
                  </li>;
          })}
            </ul>
          </>}
      </main>
      <SiteFooter data-id="element-1949" data-mp="28x6bqrkoa7zl" />
    </div>;
}